/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import moment from 'moment';

import { TimeRecordingSummary, TimeRecordingSummaryData } from '../model/time-recording-summary.model';
import { TimeRecordData, TimeRecordType, TimeRecordTypes } from '../model/time-record.model';
import { CarryoverData } from '../model/carryover.model';
import { hasValue } from '@typescript-kit/core';
import { TargetHoursData } from '../model/target-hours.model';
import { ProjectData } from '../model/project.model';

export class TimeRecordingService {

  calculateHours(from: string | moment.Moment, to: string | moment.Moment): number {
    if (!moment.isMoment(from)) {
      from = moment(from, 'HH:mm', true);
    }
    if (!moment.isMoment(to)) {
      to = moment(to, 'HH:mm', true);
    }
    if (!from.isValid() || !to.isValid()) {
      return null;
    }
    const hours = (to.hours() - from.hours()) + ((to.minutes() - from.minutes()) / 60);
    return hours >= 0 ? hours : hours + 24;
  }

  calculateTo(from: string | moment.Moment, hours: number): string {
    if (!moment.isMoment(from)) {
      from = moment(from, 'HH:mm', true);
    }
    if (!from.isValid() || typeof hours !== 'number') {
      return null;
    }
    return from.add(hours, 'hours').format('HH:mm');
  }

  calculateFrom(to: string | moment.Moment, hours: number): string {
    if (!moment.isMoment(to)) {
      to = moment(to, 'HH:mm', true);
    }
    if (!to.isValid() || typeof hours !== 'number') {
      return null;
    }
    return to.add(-hours, 'hours').format('HH:mm');
  }

  calculateActualWeekHours(timeRecordList: Iterable<TimeRecordData>, date: string): number {
    // const firstDayOfWeek = moment([+year, +month - 1, day]).startOf('isoWeek').date();
    const firstDayOfWeek = moment(date, 'YYYY-M-D').startOf('isoWeek').valueOf();
    const firstDayOfNextWeek = moment(firstDayOfWeek).add(1, 'weeks').valueOf();
    const recordsOfWeek: TimeRecordData[] = [];
    for (const timeRecord of timeRecordList) {
      const timeRecordDate = moment(timeRecord.date, 'YYYY-M-D').valueOf();
      if (timeRecordDate < firstDayOfWeek || timeRecordDate >= firstDayOfNextWeek) {
        continue;
      }
      recordsOfWeek.push(timeRecord);
    }
    return this.calculateActualHours(recordsOfWeek);
  }

  calculateActualHours(records: TimeRecordData[]): number {
    return records.reduce((total: number, record) => {
      if (!hasValue(record.hours) || !hasValue(record.type)) {
        return total;
      }
      if (record.type === TimeRecordTypes.BREAK) {
        return hasValue(total) ? total : 0;
      }
      return total ? total + record.hours : record.hours;
    }, null);
  }

  calculateTotal(timeRecordList: Iterable<TimeRecordData>, key: string): number {
    let total: number = null;
    for (const record of timeRecordList) {
      if (!hasValue(record[key])) {
        continue;
      }
      total = (total || 0) + record[key];
    }
    return total;
  }

  isWorkTime(type: TimeRecordType) {
    switch (type) {
      case TimeRecordTypes.WORK:
      case TimeRecordTypes.COMP_TIME:
      case TimeRecordTypes.SHORT_TIME_WORK:
        return true;
      default:
        return false;
    }
  }

  calculateProjectSums(timeRecords: Iterable<TimeRecordData>): Map<string, number> {
    const result = new Map<string, number>();
    for (const timeRecord of timeRecords) {
      if (!this.isWorkTime(timeRecord.type)) {
        continue;
      }
      result.set(timeRecord.projectId, (result.get(timeRecord.projectId) ?? 0) + timeRecord.hours);
    }
    return result;
  }


  mapProjectNames<T extends { projectId: string }>(entities: T[], projects: Iterable<ProjectData>, sort: boolean): (T & { projectName: string })[] {
    const projectIdToNameMapping = new Map<string, string>();
    for (const project of projects) {
      projectIdToNameMapping.set(project.id, project.name);
    }

    let results: (T & { projectName: string })[] = [];
    for (const entity of entities) {
      results.push({
        ...entity,
        projectName: projectIdToNameMapping.get(entity.projectId) ?? ''
      });
    }

    if (sort) {
      const collator: Intl.Collator = new Intl.Collator('de');
      results = results.sort((a, b) => collator.compare(a.projectName, b.projectName));
    }
    return results;
  }

  calculateSummary(
    timeRecordList: Iterable<TimeRecordData>, targetHoursList: Iterable<TargetHoursData>, carryover: CarryoverData
  ): TimeRecordingSummary {
    const summary: TimeRecordingSummaryData = {
      totalExpectedHours: null,
      totalWorkHours: null,
      totalPublicHolidayHours: null,
      totalSickHours: null,
      totalShortTimeWorkHours: null,
      totalHolidayHours: null,
      totalCompTimeHours: null,
      totalActualHours: null,
      totalSalariedHours: null,
      totalOvertime: null,
      totalOvertime50: null,
      totalOvertime100: null,
      totalFlextime: null,
      flextimeCarryover: null,
      flextimeBalance: null,
      holidayCarryover: null,
      holidayBalance: null,
    };
    if (!timeRecordList) {
      timeRecordList = [];
    }

    for (const timeRecord of timeRecordList) {
      if (hasValue(timeRecord.hours) && hasValue(timeRecord.type)) {
        const hours = timeRecord.hours;
        switch (timeRecord.type) {
          case TimeRecordTypes.WORK:
            summary.totalWorkHours = (summary.totalWorkHours || 0) + hours;
            summary.totalActualHours = (summary.totalActualHours || 0) + hours;
            break;
          case TimeRecordTypes.PUBLIC_HOLIDAY:
            summary.totalPublicHolidayHours = (summary.totalPublicHolidayHours || 0) + hours;
            summary.totalActualHours = (summary.totalActualHours || 0) + hours;
            break;
          case TimeRecordTypes.SICK:
            summary.totalSickHours = (summary.totalSickHours || 0) + hours;
            summary.totalActualHours = (summary.totalActualHours || 0) + hours;
            break;
          case TimeRecordTypes.HOLIDAY:
            summary.totalHolidayHours = (summary.totalHolidayHours || 0) + hours;
            summary.totalActualHours = (summary.totalActualHours || 0) + hours;
            break;
          case TimeRecordTypes.SHORT_TIME_WORK:
            summary.totalShortTimeWorkHours = (summary.totalShortTimeWorkHours || 0) + hours;
            summary.totalActualHours = (summary.totalActualHours || 0) + hours;
            break;
          case TimeRecordTypes.COMP_TIME:
            summary.totalCompTimeHours = (summary.totalCompTimeHours || 0) + hours;
            summary.totalActualHours = (summary.totalActualHours || 0) + hours;
            continue;
          case TimeRecordTypes.BREAK:
            // DO NOT ADD TO TOTAL HOURS
            continue;
          default:
            console.log(timeRecord);
            throw new Error('Invalid state');
        }
      }
      if (hasValue(timeRecord.overtime)) {
        summary.totalOvertime = (summary.totalOvertime || 0) + timeRecord.overtime;
      }
      if (hasValue(timeRecord.overtime50)) {
        summary.totalOvertime50 = (summary.totalOvertime50 || 0) + timeRecord.overtime50;
      }
      if (hasValue(timeRecord.overtime100)) {
        summary.totalOvertime100 = (summary.totalOvertime100 || 0) + timeRecord.overtime100;
      }
    }

    for (const targetHours of targetHoursList) {
      summary.totalExpectedHours = (summary.totalExpectedHours || 0) + targetHours.value;
    }

    // ALTERNATIVE 1: NEGATIVE FLEXTIME NOT ALLOWED
    summary.totalSalariedHours = (summary.totalActualHours || 0)
      - (summary.totalOvertime || 0)
      - (summary.totalOvertime50 || 0)
      - (summary.totalOvertime100 || 0);

    if (hasValue(summary.totalExpectedHours) && summary.totalExpectedHours <= summary.totalSalariedHours) {
      summary.totalFlextime = summary.totalSalariedHours - summary.totalExpectedHours;
      summary.totalSalariedHours = summary.totalExpectedHours;
      // OPTIONAL: ASSOCIATE COMP TIME TO FLEXTIME
      // } else if (!hasValue(summary.totalExpectedHours) && hasValue(summary.totalCompTimeHours)) {
      //   summary.totalFlextime = summary.totalCompTimeHours;
      //   summary.totalSalariedHours = summary.totalSalariedHours - summary.totalCompTimeHours;
    }

    // ALTERNATIVE 2: NEGATIVE FLEXTIME ALLOWED
    // if (hasValue(summary.totalExpectedHours)) {
    //   summary.totalSalariedHours = summary.totalExpectedHours;
    //   summary.totalFlextime = (summary.totalActualHours || 0)
    //     - (summary.totalOvertime || 0)
    //     - (summary.totalOvertime50 || 0)
    //     - (summary.totalOvertime100 || 0)
    //     - summary.totalExpectedHours;
    //   // OPTIONAL: ASSOCIATE COMP TIME TO FLEXTIME
    //   // } else if (hasValue(summary.totalCompTimeHours)) {
    //   //   summary.totalSalariedHours = (summary.totalActualHours || 0)
    //   //     - (summary.totalOvertime || 0)
    //   //     - (summary.totalOvertime50 || 0)
    //   //     - (summary.totalOvertime100 || 0)
    //   //     - summary.totalCompTimeHours;
    //   //   summary.totalFlextime = summary.totalCompTimeHours;
    // } else {
    //   summary.totalSalariedHours = (summary.totalActualHours || 0)
    //     - (summary.totalOvertime || 0)
    //     - (summary.totalOvertime50 || 0)
    //     - (summary.totalOvertime100 || 0);
    //   summary.totalFlextime = null;
    // }

    summary.totalHours = hasValue(summary.totalActualHours) || hasValue(summary.totalFlextime)
      ? (summary.totalActualHours || 0) - (summary.totalFlextime || 0)
      : null;

    if (carryover) {
      if (hasValue(carryover.flextimeBalance && carryover.flextimeBalance.value)) {
        summary.flextimeCarryover = carryover.flextimeBalance.value;
        summary.flextimeBalance = summary.flextimeCarryover - (summary.totalCompTimeHours || 0) + (summary.totalFlextime || 0);
      }
      if (hasValue(carryover.holidayBalance && carryover.holidayBalance.value)) {
        summary.holidayCarryover = carryover.holidayBalance.value;
        summary.holidayBalance = summary.holidayCarryover - (summary.totalHolidayHours || 0);
      }
    }
    return new TimeRecordingSummary(summary);
  }

}
